import type { CategoryScores } from './quizData';

type ScoreLevel = 'low' | 'medium' | 'high';

function getLevel(score: number): ScoreLevel {
  if (score <= 8) return 'low';
  if (score <= 14) return 'medium';
  return 'high';
}

const categoryExplanations: Record<string, Record<ScoreLevel, string>> = {
  visibility: {
    low: 'Your current content is not creating enough reach or awareness to consistently attract attention.',
    medium: 'You have some visibility, but your current approach is not yet generating enough momentum.',
    high: 'Your content is creating visibility effectively and giving your brand stronger exposure.',
  },
  consistency: {
    low: 'Your posting rhythm is too inconsistent to build trust and top-of-mind awareness.',
    medium: 'You are posting often enough to create some momentum, but not enough for reliable growth.',
    high: 'Your consistency is strong and supports audience trust and algorithmic momentum.',
  },
  authority: {
    low: 'Your content is not doing enough to establish expertise, credibility, or trust.',
    medium: 'Your content shows some expertise, but it could do more to position you as the obvious choice.',
    high: 'Your content is building authority well and helping your brand earn trust.',
  },
  leadGeneration: {
    low: 'Your content is not currently functioning as a reliable inbound lead source.',
    medium: 'Your content is creating some lead opportunities, but the system is not yet dependable.',
    high: 'Your content is successfully helping drive inbound leads and interest.',
  },
  conversion: {
    low: 'Your content does not lead people clearly into a next step, which weakens business results.',
    medium: 'You have some conversion structure, but the path from attention to action is not yet strong enough.',
    high: 'Your content has a clear next step and supports conversion effectively.',
  },
};

export function getCategoryExplanation(key: string, score: number): string {
  return categoryExplanations[key]?.[getLevel(score)] ?? '';
}

const categoryLabelsMap: Record<string, string> = {
  visibility: 'Visibility',
  consistency: 'Consistency',
  authority: 'Authority',
  leadGeneration: 'Lead Generation',
  conversion: 'Conversion',
};

export function getCategoryLabel(key: string): string {
  return categoryLabelsMap[key] ?? key;
}

interface WeakArea {
  name: string;
  whyItMatters: string;
  improvements: string[];
}

const weakAreaData: Record<string, { why: string; improvements: string[] }> = {
  visibility: {
    why: 'If people are not seeing your content, they cannot buy from you.',
    improvements: [
      'Focus on stronger hooks in the first 3 seconds',
      'Publish more educational and opinion-driven content',
      'Improve consistency and distribution across platforms',
    ],
  },
  consistency: {
    why: 'Consistent posting builds trust, familiarity, and visibility over time.',
    improvements: [
      'Commit to at least 3 posts per week',
      'Create a simple weekly content calendar',
      'Batch content creation in advance to stay ahead',
    ],
  },
  authority: {
    why: 'Without authority, your audience has no reason to choose you over anyone else.',
    improvements: [
      'Share more case studies and client results',
      'Create educational content that teaches, not just sells',
      'Share behind-the-scenes and expertise-driven insights',
    ],
  },
  leadGeneration: {
    why: 'If your content is not generating leads, it is costing you money without return.',
    improvements: [
      'Create lead magnets aligned with your content topics',
      'Add clear calls to action in every piece of content',
      'Build a system that captures interest and follows up',
    ],
  },
  conversion: {
    why: 'Attention without a next step rarely turns into leads or revenue.',
    improvements: [
      'Add a CTA to each post directing viewers to one clear action',
      'Send viewers to a landing page, DM prompt, or booking link',
      'Align all content with one primary offer or service',
    ],
  },
};

export function getWeakAreas(categories: CategoryScores): WeakArea[] {
  const entries = Object.entries(categories) as [string, number][];
  entries.sort((a, b) => a[1] - b[1]);
  return entries.slice(0, 3).map(([key]) => ({
    name: getCategoryLabel(key),
    whyItMatters: weakAreaData[key]?.why ?? '',
    improvements: weakAreaData[key]?.improvements ?? [],
  }));
}

export function getDynamicRecommendation(answers: Record<string, string | string[]>, categories: CategoryScores): string {
  const freq = answers.posting_frequency as string;
  const cta = answers.clear_cta as string;

  if (freq === '0' || freq === '1–2') {
    return 'increase consistency before trying to scale reach';
  }
  if (categories.leadGeneration <= 8 && (cta === 'No' || cta === 'Sometimes')) {
    return 'build a stronger conversion path after each piece of content';
  }
  if (categories.authority <= 8) {
    return 'shift content toward education, proof, and case studies';
  }
  const types = (answers.content_types as string[]) || [];
  if (types.length === 1 && types.includes('Promotional')) {
    return 'reduce sales-heavy posting and increase trust-building content';
  }
  return 'optimize your current system with clearer CTAs and more trust-building content';
}

export function getPdfDiagnosis(score: number): string {
  if (score < 50)
    return 'Your business does not yet have a structured content system. Growth is being slowed by inconsistency, weak conversion paths, and lack of strategic direction.';
  if (score <= 75)
    return 'Your business has some traction, but important gaps in consistency, conversion, and authority are limiting growth.';
  return 'Your business has a strong content foundation. With the right optimizations, you can scale visibility, trust, and inbound opportunities much faster.';
}
